import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
    IconButton,
    Button,
    Paper,
    Typography,
    Box,
    CircularProgress,
    Alert,
    Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import aiService from '../../../../../services/api/ai.service';
import { getValidations } from '../../../../../services/validations.service';
import { QUESTION_INPUT_TYPE } from '../../../../../types/question.type';
import axios from '../../../../../axios';
import styles from './AiChatSidebar.module.scss';
import ExplainWhyDialog from './ExplainWhyDialog';
import AIIcon from '../../../../../assets/svgs/AI-Icon.svg';
import useAccountId from '../../../../../hooks/useAccountId';

// Helper function to convert word numbers to digits (e.g., "one" -> 1, "twelve" -> 12)
const convertWordsToNumbers = (text) => {
    if (typeof text !== 'string') return text;

    const wordToNumber = {
        'zero': 0, 'one': 1, 'two': 2, 'three': 3, 'four': 4, 'five': 5,
        'six': 6, 'seven': 7, 'eight': 8, 'nine': 9, 'ten': 10,
        'eleven': 11, 'twelve': 12, 'thirteen': 13, 'fourteen': 14, 'fifteen': 15,
        'sixteen': 16, 'seventeen': 17, 'eighteen': 18, 'nineteen': 19, 'twenty': 20,
        'thirty': 30, 'forty': 40, 'fifty': 50, 'sixty': 60, 'seventy': 70,
        'eighty': 80, 'ninety': 90, 'hundred': 100, 'thousand': 1000
    };

    let result = text.toLowerCase();

    // Replace word numbers with digits
    Object.entries(wordToNumber).forEach(([word, number]) => {
        const regex = new RegExp(`\\b${word}\\b`, 'gi');
        result = result.replace(regex, number.toString());
    });

    return result;
};

// Helper function to check if a question supports validation options (can create new options)
const isValidationQuestion = (question) => {
    const isValidation = question?.question?.type === 'validation' ||
        (question?.question?.customQuestionInputType &&
            question?.question?.customQuestionInputType === QUESTION_INPUT_TYPE.UNLIMITED_ANSWERS);

    return isValidation;
};

// Helper function to check if a question has predefined options (can only match existing)
const hasPredefinedOptions = (question) => {
    const inputType = question?.question?.customQuestionInputType;
    // Safety check: if inputType is undefined, return false to prevent infinite loops
    if (!inputType) {
        return false;
    }
    return inputType === QUESTION_INPUT_TYPE.LIMITED_ANSWERS ||
        inputType === QUESTION_INPUT_TYPE.NUMBER_RANGE_LIMITED_ANSWERS;
};

// Helper function to check if a question supports multiple values
const isMultipleValueQuestion = (question) => {
    return question?.question?.multiple === true;
};

// Helper function to check predefined options and find best match
const checkPredefinedOptionsAndFindMatch = (value, question, findBestMatch) => {


    // Safety check: prevent infinite loops
    if (!question || !question.question) {
        return { exists: true, matchType: 'invalid_question' };
    }

    if (!hasPredefinedOptions(question)) {
        return { exists: true, matchType: 'not_predefined' };
    }

    // Get predefined options from customQuestionOptions
    // Handle both object format and string array format
    let predefinedOptions = [];

    // Try both possible paths for customQuestionOptions
    const customQuestionOptions = question?.customQuestionOptions || question?.question?.customQuestionOptions;

    if (customQuestionOptions && customQuestionOptions.length > 0) {
        if (typeof customQuestionOptions[0] === 'string') {
            // String array format: ['option 1', 'option 2'] or ['1', '2', '3']
            predefinedOptions = customQuestionOptions;
        } else {
            // Object format: Handle mixed types for NUMBER_RANGE_LIMITED_ANSWERS
            const questionType = question?.customQuestionInputType || question?.question?.customQuestionInputType;


            if (questionType === QUESTION_INPUT_TYPE.LIMITED_ANSWERS) {
                predefinedOptions = customQuestionOptions
                    .filter(option => option?.type === QUESTION_INPUT_TYPE.LIMITED_ANSWERS && option?.option)
                    .map(option => option.option);
            } else if (questionType === QUESTION_INPUT_TYPE.NUMBER_RANGE_LIMITED_ANSWERS) {
                // For NUMBER_RANGE_LIMITED_ANSWERS, only use the actual predefined options
                // Do NOT generate range options - only use what's explicitly defined
                predefinedOptions = [];


                // Safety check: prevent infinite loops
                if (Array.isArray(customQuestionOptions)) {
                    customQuestionOptions.forEach((opt, index) => {
                        // Safety check: prevent infinite loops
                        if (index > 100) {

                            return;
                        }


                        if (opt && typeof opt === 'object') {
                            if (opt.type === 'limitedAnswers' && opt.option) {
                                // For limitedAnswers, convert option to number if possible
                                const numValue = Number(opt.option);

                                if (!Number.isNaN(numValue)) {
                                    predefinedOptions.push(numValue.toString());

                                }
                            }
                            // Note: We intentionally skip 'numberRange' type - we don't generate range options
                            // The AI should only work with explicitly defined options
                        }
                    });
                } else {
                    console.warn(`❌ customQuestionOptions is not an array:`, typeof customQuestionOptions);
                }

                // Remove duplicates and sort
                predefinedOptions = [...new Set(predefinedOptions)].sort((a, b) => Number(a) - Number(b));

            }
        }
    }



    if (predefinedOptions.length === 0) {

        return { exists: true, matchType: 'no_predefined_options' };
    }



    if (question?.customQuestionInputType === QUESTION_INPUT_TYPE.NUMBER_RANGE_LIMITED_ANSWERS || question?.question?.customQuestionInputType === QUESTION_INPUT_TYPE.NUMBER_RANGE_LIMITED_ANSWERS) {


        // Convert value to number for comparison (including word-to-number conversion)
        let numericValue = null;
        if (typeof value === 'number') {
            numericValue = value;
        } else if (typeof value === 'string') {
            // Convert word numbers to digits first
            const convertedValue = convertWordsToNumbers(value);

            const match = convertedValue.match(/-?\d+(?:\.\d+)?/);
            if (match) {
                numericValue = Number(match[0]);
            }
        }



        if (numericValue === null || Number.isNaN(numericValue)) {

            return { exists: false, matchType: 'invalid_number', originalValue: value };
        }

        // Convert predefined options to numbers
        const numericOptions = predefinedOptions.map(opt => Number(opt)).filter(num => !Number.isNaN(num));


        // Check for exact numeric match
        const exactMatch = numericOptions.find(option => option === numericValue);


        if (exactMatch !== undefined) {

            return {
                exists: true,
                matchType: 'exact',
                matchedOption: { label: exactMatch.toString(), _id: exactMatch.toString() }
            };
        }

        // For numeric fields, we don't do fuzzy matching - either exact match or no match

        return { exists: false, matchType: 'no_match', originalValue: value };
    }

    // For LIMITED_ANSWERS, handle text matching
    // First check for exact match (case-insensitive)
    const exactMatch = predefinedOptions.find(option =>
        String(option).trim().toLowerCase() === String(value).trim().toLowerCase()
    );

    if (exactMatch) {
        return {
            exists: true,
            matchType: 'exact',
            matchedOption: { label: exactMatch, _id: exactMatch }
        };
    }

    // If no exact match, try fuzzy matching
    const bestMatch = findBestMatch(String(value), predefinedOptions.map(opt => ({ label: opt, _id: opt })), 0.6);

    if (bestMatch) {
        return {
            exists: false, // Original value doesn't exist in predefined options
            matchType: 'fuzzy',
            matchedOption: bestMatch,
            originalValue: value,
            suggestedValue: bestMatch.label,
            similarity: bestMatch.similarity
        };
    }

    // No match found in predefined options
    return {
        exists: false,
        matchType: 'no_match',
        originalValue: value
    };
};

// Individual suggestion item component
const SuggestionItem = ({
    fieldAccessor,
    fieldLabel,
    displayValue,
    suggestion,
    question,
    availableFields,
    checkValidationValueAndFindMatch,
    checkPredefinedOptionsAndFindMatch,
    findBestMatch,
    onFuzzyMatchDecision,
    isIgnored = false,
    onToggleIgnore,
    onExplainWhy,
    onReportCreateNew
}) => {
    const [validationResult, setValidationResult] = React.useState(null);
    const [isChecking, setIsChecking] = React.useState(false);
    const [fuzzyDecision, setFuzzyDecision] = React.useState(null); // 'apply' or 'cancel'
    const prevCreateNewStatus = React.useRef(null);

    React.useEffect(() => {
        const checkValue = async () => {
            // Check if this question supports validation options or has predefined options
            const isValidation = isValidationQuestion(question);
            const hasPredefined = hasPredefinedOptions(question);

            if (isValidation) {
                setIsChecking(true);
                try {
                    const result = await checkValidationValueAndFindMatch(suggestion.value, question);
                    setValidationResult(result);
                } catch (error) {
                    console.error('Error checking validation value:', error);
                    setValidationResult({ exists: true, matchType: 'error' }); // Assume exists on error
                } finally {
                    setIsChecking(false);
                }
            } else if (hasPredefined) {
                setIsChecking(true);
                try {
                    const result = checkPredefinedOptionsAndFindMatch(suggestion.value, question, findBestMatch);
                    setValidationResult(result);
                } catch (error) {
                    console.error('Error checking predefined options:', error);
                    setValidationResult({ exists: true, matchType: 'error' }); // Assume exists on error
                } finally {
                    setIsChecking(false);
                }
            } else {
                setValidationResult({ exists: true, matchType: 'not_validation' }); // Non-validation fields always "exist"
            }
        };

        checkValue();
    }, [suggestion.value, question, checkValidationValueAndFindMatch, checkPredefinedOptionsAndFindMatch, findBestMatch]);

    // Report to parent when create new status changes
    React.useEffect(() => {
        if (!onReportCreateNew) return;

        const isValidation = isValidationQuestion(question);
        const hasPredefined = hasPredefinedOptions(question);
        let willCreateNew = null;

        // Case 1: No question object but field is validation type
        if (!question) {
            const field = availableFields?.find(f => f.accessor === fieldAccessor);
            if (field?.type === 'validation' || field?.validationType) {
                willCreateNew = true;
            }
        }
        // Case 2: Based on validation result and fuzzy decision
        else if (validationResult && (isValidation || hasPredefined)) {
            // Check fuzzy decision first
            if (fuzzyDecision === 'cancel') {
                // User rejected suggestion, will create new
                willCreateNew = true;
            } else if (fuzzyDecision === 'apply') {
                // User accepted suggestion, won't create new
                willCreateNew = false;
            } else if (validationResult.matchType === 'no_match' ||
                (validationResult.matchType === 'fuzzy' && validationResult.similarity < 0.9)) {
                // Will create new by default (only for validation, not predefined)
                willCreateNew = isValidation ? true : false;
            } else if (validationResult.matchType === 'exact' ||
                (validationResult.matchType === 'fuzzy' && validationResult.similarity >= 0.9)) {
                // Won't create new (exact match or high similarity)
                willCreateNew = false;
            }
        }

        // Only call onReportCreateNew if status changed
        if (willCreateNew !== null && willCreateNew !== prevCreateNewStatus.current) {
            prevCreateNewStatus.current = willCreateNew;
            onReportCreateNew(fieldAccessor, willCreateNew);
        }
    }, [validationResult, fuzzyDecision, question, fieldAccessor, availableFields, onReportCreateNew]);

    const getStatusIndicator = () => {


        // Check if this question supports validation options or has predefined options
        const isValidation = isValidationQuestion(question);
        const hasPredefined = hasPredefinedOptions(question);

        // Debug logging for dx field

        // If question is not found but field is a validation field, show create new
        if (!question) {
            // Check if this is a validation field based on availableFields
            const field = availableFields?.find(f => f.accessor === fieldAccessor);
            if (field?.type === 'validation' || field?.validationType) {
                return (
                    <div className={styles.createNewIndicator}>
                        <AutoAwesomeIcon />
                        <Typography variant="caption">
                            Create new
                        </Typography>
                    </div>
                );
            }
        }

        if (!isValidation && !hasPredefined) {
            return null;
        }

        if (isChecking) {
            return (
                <Box display="flex" alignItems="center" gap={0.5}>
                    <CircularProgress size={12} />
                    <Typography variant="caption" color="textSecondary">
                        Checking...
                    </Typography>
                </Box>
            );
        }

        if (!validationResult) return null;

        // Show fuzzy match suggestion with Apply/Cancel actions only if similarity >= 90%
        if (validationResult.matchType === 'fuzzy' && validationResult.similarity >= 0.9) {
            const handleApplyFuzzy = () => {
                setFuzzyDecision('apply');
                if (onFuzzyMatchDecision) {
                    onFuzzyMatchDecision(fieldAccessor, 'apply', validationResult);
                }
            };

            const handleCancelFuzzy = () => {
                setFuzzyDecision('cancel');
                if (onFuzzyMatchDecision) {
                    onFuzzyMatchDecision(fieldAccessor, 'cancel', validationResult);
                }
            };

            return (
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={0.5}
                    sx={{
                        backgroundColor: '#e3f2fd',
                        border: '1px solid #2196f3',
                        borderRadius: '8px',
                        padding: '6px 8px',
                        marginTop: '6px'
                    }}
                >
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <AutoAwesomeIcon sx={{ fontSize: 12, color: '#1976d2' }} />
                        <Typography variant="caption" sx={{ color: '#1565c0', fontWeight: 600 }}>
                            Suggested match ({Math.round(validationResult.similarity * 100)}%)
                        </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: '#0d47a1', fontStyle: 'italic' }}>
                        "{validationResult.originalValue}" → "{validationResult.suggestedValue}"
                    </Typography>

                    {!fuzzyDecision && (
                        <>
                            <Box display="flex" gap={1} marginTop={0.5}>
                                <IconButton
                                    size="small"
                                    onClick={handleApplyFuzzy}
                                    sx={{
                                        backgroundColor: '#4caf50',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: '#45a049'
                                        },
                                        padding: '4px'
                                    }}
                                    title="Apply suggested match"
                                >
                                    <CheckIcon sx={{ fontSize: 12 }} />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={handleCancelFuzzy}
                                    sx={{
                                        backgroundColor: '#f44336',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: '#da190b'
                                        },
                                        padding: '4px'
                                    }}
                                    title="Create new option instead"
                                >
                                    <CancelIcon sx={{ fontSize: 12 }} />
                                </IconButton>
                            </Box>
                            <Typography variant="caption" sx={{ color: '#666', fontSize: '11px', marginTop: 0.5, display: 'block' }}>
                                {validationResult.similarity >= 0.9
                                    ? `Default: Will use suggested match (${Math.round(validationResult.similarity * 100)}% match)`
                                    : `Default: Will create new option (${Math.round(validationResult.similarity * 100)}% match too low)`
                                }
                            </Typography>
                        </>
                    )}

                    {fuzzyDecision === 'apply' && (
                        <Box display="flex" alignItems="center" gap={0.5} marginTop={0.5}>
                            <CheckIcon sx={{ fontSize: 12, color: '#4caf50' }} />
                            <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 600 }}>
                                Will use suggested match
                            </Typography>
                        </Box>
                    )}

                    {fuzzyDecision === 'cancel' && (
                        <Box display="flex" alignItems="center" gap={0.5} marginTop={0.5}>
                            <CancelIcon sx={{ fontSize: 12, color: '#f44336' }} />
                            <Typography variant="caption" sx={{ color: '#f44336', fontWeight: 600 }}>
                                Will create new option
                            </Typography>
                        </Box>
                    )}
                </Box>
            );
        }

        // Show create new for no matches or fuzzy matches with low similarity (only for validation questions)
        if (validationResult.matchType === 'no_match' ||
            (validationResult.matchType === 'fuzzy' && validationResult.similarity < 0.9)) {
            if (isValidation) {
                return (
                    <div className={styles.createNewIndicator}>
                        <AutoAwesomeIcon />
                        <Typography variant="caption">
                            Create new
                        </Typography>
                    </div>
                );
            } else if (hasPredefined) {
                return (
                    <div className={styles.createNewIndicator}>
                        <AutoAwesomeIcon />
                        <Typography variant="caption">
                            Not in predefined options
                        </Typography>
                    </div>
                );
            }
        }

        return null;
    };

    return (
        <div className={`${styles.suggestionItem} ${isIgnored ? styles.ignoredSuggestion : ''}`}>
            <div className={styles.suggestionContent}>
                <div className={styles.fieldInfo}>
                    <Typography variant="caption" fontWeight="bold" sx={{
                        color: isIgnored ? 'text.disabled' : 'inherit',
                        textDecoration: isIgnored ? 'line-through' : 'none'
                    }}>
                        {fieldLabel}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5}>
                        {onExplainWhy && !isIgnored && (
                            <IconButton
                                size="small"
                                onClick={() => onExplainWhy(fieldAccessor, suggestion, fieldLabel)}
                                sx={{
                                    padding: '4px',
                                    color: 'primary.main',
                                    '&:hover': {
                                        backgroundColor: 'primary.light',
                                        color: 'primary.dark'
                                    }
                                }}
                                title="Explain why this value was suggested"
                            >
                                <HelpOutlineIcon fontSize="small" />
                            </IconButton>
                        )}
                        {onToggleIgnore && (
                            <IconButton
                                size="small"
                                onClick={() => onToggleIgnore(fieldAccessor)}
                                sx={{
                                    padding: '4px',
                                    color: isIgnored ? 'text.disabled' : 'text.secondary',
                                    '&:hover': {
                                        backgroundColor: isIgnored ? 'rgba(0, 0, 0, 0.04)' : 'rgba(0, 0, 0, 0.08)'
                                    }
                                }}
                                title={isIgnored ? 'Restore suggestion' : 'Ignore suggestion'}
                            >
                                {isIgnored ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                            </IconButton>
                        )}
                    </Box>
                </div>
                <Typography variant="body2" color={isIgnored ? "text.disabled" : "textSecondary"} sx={{
                    textDecoration: isIgnored ? 'line-through' : 'none'
                }}>
                    <strong>Value:</strong> {displayValue}
                </Typography>
                {suggestion.explanation && !isIgnored && (
                    <Typography variant="caption" color="textSecondary" style={{ fontStyle: 'italic' }}>
                        {suggestion.explanation}
                    </Typography>
                )}
                {!isIgnored && getStatusIndicator()}
                {isIgnored && (
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        sx={{
                            backgroundColor: '#f5f5f5',
                            border: '1px solid #e0e0e0',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            marginTop: '4px'
                        }}
                    >
                        <VisibilityOffIcon sx={{ fontSize: 12, color: '#9e9e9e' }} />
                        <Typography variant="caption" sx={{ color: '#9e9e9e', fontStyle: 'italic' }}>
                            Ignored - will not be applied
                        </Typography>
                    </Box>
                )}
            </div>
        </div>
    );
};

const AiChatSidebar = ({
    formContext,
    availableFields,
    onApplySuggestions,
    facilityId,
    disabled = false,
    onClose,
    questions = [],
    refreshComponent,
    aiData,
    onAiDataUpdate,
    aiDataLoading
}) => {
    const [promptValue, setPromptValue] = useState('');
    const [promptLoading, setPromptLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentSuggestions, setCurrentSuggestions] = useState(null);
    const [isEditingPrompt, setIsEditingPrompt] = useState(false);
    const [validationOptionsCache, setValidationOptionsCache] = useState({});
    const [fuzzyDecisions, setFuzzyDecisions] = useState({}); // Track user decisions for fuzzy matches
    const [isCreatingNewOptions, setIsCreatingNewOptions] = useState(false); // Track creation state
    const [ignoredSuggestions, setIgnoredSuggestions] = useState(new Set()); // Track ignored suggestions
    const [showSuccessMessage, setShowSuccessMessage] = useState(false); // Track success message display
    const [successMessage, setSuccessMessage] = useState(''); // Success message content
    const [explainDialogOpen, setExplainDialogOpen] = useState(false); // Track explain dialog state
    const [explainDialogData, setExplainDialogData] = useState(null); // Data for explain dialog
    const [fieldsWithCreateNew, setFieldsWithCreateNew] = useState(new Set()); // Track fields that will create new options
    const promptRef = useRef(null);
    const { accountId: accountIdFromHook } = useAccountId();
    // Get auth info and active facilities for creating validation options
    const { auth, activeFacilities } = useSelector(({ auth, activeFacilities }) => ({
        auth,
        activeFacilities: activeFacilities.facilities
    }));

    // Load existing AI data when component mounts or aiData changes
    React.useEffect(() => {
        if (aiData && !currentSuggestions) {
            setPromptValue(aiData.aiPrompt || '');
            setCurrentSuggestions({
                prompt: aiData.aiPrompt || '',
                summary: aiData.aiSummary || 'AI has analyzed your prompt and found field suggestions.',
                suggestions: aiData.aiSuggestions || {},
                reasoning: aiData.aiReasoning || '',
                timestamp: aiData.createdAt ? new Date(aiData.createdAt) : new Date()
            });
        }
    }, [aiData, currentSuggestions]);

    // Helper function to process date values and add current year if not provided
    const processDateValue = (dateValue) => {
        if (!dateValue) return dateValue;

        // Check if the date value already contains a year (4 digits)
        const yearRegex = /\b(19|20)\d{2}\b/;
        if (yearRegex.test(dateValue)) {
            return dateValue; // Year already present
        }

        // Try to parse common date formats and add current year
        const currentYear = new Date().getFullYear();

        // Handle formats like "MM/DD", "MM-DD", "MM.DD"
        const monthDayRegex = /^(\d{1,2})[/\-.](\d{1,2})$/;
        const monthDayMatch = dateValue.match(monthDayRegex);
        if (monthDayMatch) {
            const [, month, day] = monthDayMatch;
            return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${currentYear}`;
        }

        // Handle formats like "Month DD", "Mon DD", "MM DD"
        const monthNameRegex = /^(january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\s+(\d{1,2})$/i;
        const monthNameMatch = dateValue.match(monthNameRegex);
        if (monthNameMatch) {
            const [, monthName, day] = monthNameMatch;
            const monthNumber = getMonthNumber(monthName);
            if (monthNumber) {
                return `${monthNumber}/${day.padStart(2, '0')}/${currentYear}`;
            }
        }

        // If no recognizable pattern, return as is
        return dateValue;
    };

    // Helper function to process time values and convert to 12-hour AM/PM format
    const processTimeValue = (timeValue) => {
        if (!timeValue) return timeValue;

        // First, extract time from string that might contain date and time
        const timeMatch = timeValue.match(/(\d{1,2}:\d{2}(?::\d{2})?\s*(am|pm|AM|PM)?)/i);
        if (timeMatch) {
            timeValue = timeMatch[0]; // Use only the time portion
        }

        // If already in 12-hour format (contains am/pm), return as is
        if (/am|pm/i.test(timeValue)) {
            return timeValue;
        }

        // Try to parse 24-hour format and convert to 12-hour
        const time24Regex = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;
        const time24Match = timeValue.match(time24Regex);

        if (time24Match) {
            let [, hours, minutes] = time24Match;
            hours = parseInt(hours, 10);

            // Convert to 12-hour format
            let period = 'am';
            if (hours === 0) {
                hours = 12;
            } else if (hours === 12) {
                period = 'pm';
            } else if (hours > 12) {
                hours = hours - 12;
                period = 'pm';
            }

            // Format the time
            const timeString = `${hours}:${minutes} ${period}`;
            return timeString;
        }

        // Try to parse formats like "14:30", "2:30 PM", etc.
        const timeWithPeriodRegex = /^(\d{1,2}):(\d{2})\s*(am|pm)$/i;
        const timeWithPeriodMatch = timeValue.match(timeWithPeriodRegex);

        if (timeWithPeriodMatch) {
            const [, hours, minutes, period] = timeWithPeriodMatch;
            return `${hours}:${minutes} ${period.toLowerCase()}`;
        }

        // If no recognizable pattern, return as is
        return timeValue;
    };

    // Helper function to convert month name to number
    const getMonthNumber = (monthName) => {
        const months = {
            'january': '01', 'jan': '01',
            'february': '02', 'feb': '02',
            'march': '03', 'mar': '03',
            'april': '04', 'apr': '04',
            'may': '05',
            'june': '06', 'jun': '06',
            'july': '07', 'jul': '07',
            'august': '08', 'aug': '08',
            'september': '09', 'sep': '09',
            'october': '10', 'oct': '10',
            'november': '11', 'nov': '11',
            'december': '12', 'dec': '12'
        };
        return months[monthName.toLowerCase()];
    };

    // Helper function to calculate string similarity (Levenshtein distance based)
    const calculateSimilarity = (str1, str2) => {
        const s1 = str1.toLowerCase().trim();
        const s2 = str2.toLowerCase().trim();

        // Exact match
        if (s1 === s2) return 1.0;

        // Check if one string contains the other
        if (s1.includes(s2) || s2.includes(s1)) {
            return 0.9;
        }

        // Remove common prefixes/suffixes like "Dr.", "Mr.", etc.
        const cleanStr1 = s1.replace(/^(dr\.?|mr\.?|ms\.?|mrs\.?)\s*/i, '');
        const cleanStr2 = s2.replace(/^(dr\.?|mr\.?|ms\.?|mrs\.?)\s*/i, '');

        if (cleanStr1 === cleanStr2) return 0.95;
        if (cleanStr1.includes(cleanStr2) || cleanStr2.includes(cleanStr1)) {
            return 0.85;
        }

        // Enhanced medical term matching
        // Check for medical terms with different prefixes/contexts
        const medicalTermMatch = checkMedicalTermSimilarity(cleanStr1, cleanStr2);
        if (medicalTermMatch > 0) {
            return medicalTermMatch;
        }

        // Calculate Levenshtein distance
        const matrix = Array(cleanStr2.length + 1).fill(null).map(() => Array(cleanStr1.length + 1).fill(null));

        for (let i = 0; i <= cleanStr1.length; i++) matrix[0][i] = i;
        for (let j = 0; j <= cleanStr2.length; j++) matrix[j][0] = j;

        for (let j = 1; j <= cleanStr2.length; j++) {
            for (let i = 1; i <= cleanStr1.length; i++) {
                const indicator = cleanStr1[i - 1] === cleanStr2[j - 1] ? 0 : 1;
                matrix[j][i] = Math.min(
                    matrix[j][i - 1] + 1, // deletion
                    matrix[j - 1][i] + 1, // insertion
                    matrix[j - 1][i - 1] + indicator // substitution
                );
            }
        }

        const distance = matrix[cleanStr2.length][cleanStr1.length];
        const maxLength = Math.max(cleanStr1.length, cleanStr2.length);
        return maxLength === 0 ? 1 : (maxLength - distance) / maxLength;
    };

    // Helper function to check medical term similarity
    const checkMedicalTermSimilarity = (str1, str2) => {
        // Split strings into words
        const words1 = str1.split(/\s+/);
        const words2 = str2.split(/\s+/);

        // Find common medical terms (longer words that are likely medical terms)
        const medicalWords1 = words1.filter(word => word.length > 4);
        const medicalWords2 = words2.filter(word => word.length > 4);

        // Check if any medical words match exactly
        for (const word1 of medicalWords1) {
            for (const word2 of medicalWords2) {
                if (word1 === word2) {
                    // Calculate similarity based on how much of the strings match
                    const commonLength = word1.length;
                    const totalLength = Math.max(str1.length, str2.length);
                    const baseSimilarity = commonLength / totalLength;

                    // Boost similarity if the common word is significant
                    if (baseSimilarity > 0.3) {
                        const finalSimilarity = Math.min(0.8, baseSimilarity + 0.3);
                        return finalSimilarity;
                    }
                }
            }
        }

        // Check for partial medical term matches (e.g., "hypotension" in both)
        for (const word1 of medicalWords1) {
            for (const word2 of medicalWords2) {
                if (word1.includes(word2) || word2.includes(word1)) {
                    const commonLength = Math.min(word1.length, word2.length);
                    const totalLength = Math.max(str1.length, str2.length);
                    const baseSimilarity = commonLength / totalLength;

                    if (baseSimilarity > 0.2) {
                        const finalSimilarity = Math.min(0.75, baseSimilarity + 0.2);
                        return finalSimilarity;
                    }
                }
            }
        }

        return 0;
    };

    // Helper function to find best matching validation option
    const findBestMatch = (inputValue, validationOptions, threshold = 0.6) => {
        if (!validationOptions || !inputValue) return null;

        let bestMatch = null;
        let bestScore = 0;

        validationOptions.forEach(option => {
            const similarity = calculateSimilarity(inputValue, option.label);
            if (similarity > bestScore && similarity >= threshold) {
                bestScore = similarity;
                bestMatch = {
                    ...option,
                    similarity: similarity,
                    isExactMatch: similarity === 1.0,
                    isFuzzyMatch: similarity >= threshold && similarity < 1.0
                };
            }
        });

        return bestMatch;
    };


    // Helper function to check validation value and find best match
    const checkValidationValueAndFindMatch = async (value, question) => {
        if (!isValidationQuestion(question)) {
            return { exists: true, matchType: 'not_validation' }; // Not a validation field
        }

        // For UNLIMITED_ANSWERS questions, we need to get the validation type from the question
        let validationType, validationBase;
        if (question?.question?.type === 'validation') {
            validationType = question.question.validationType;
            validationBase = question.question.validationBase;
        } else if (question?.question?.customQuestionInputType === QUESTION_INPUT_TYPE.UNLIMITED_ANSWERS) {
            // For UNLIMITED_ANSWERS, use the question's validationType (same as regular validation questions)
            // The question object structure is different - validationType is on the question itself, not nested
            validationType = question.question?.validationType || question.validationType;
            validationBase = question.question?.validationBase || question.validationBase;
        } else {
            return { exists: true, matchType: 'not_validation' };
        }

        // Check if validation type is valid
        if (!validationType) {
            return { exists: true, matchType: 'no_validation_type' };
        }

        const cacheKey = `${validationType}_${validationBase || ''}`;

        try {
            let validationOptions = validationOptionsCache[cacheKey];

            if (!validationOptions) {
                // Fetch validation options if not cached

                const validationResponse = await getValidations({
                    type: validationType,
                    facilityid: facilityId,
                    validationBase: validationBase,
                    active: true
                });

                // The API returns the data directly, not wrapped in a data property
                validationOptions = validationResponse || [];

                console.log('=== API VALIDATION RESPONSE ===');
                console.log('API Response:', validationResponse);
                console.log('Validation options count:', validationOptions.length);
                console.log('First few options:', validationOptions.slice(0, 5).map(opt => opt.label));
                console.log('================================');

                // Cache the options
                setValidationOptionsCache(prev => ({
                    ...prev,
                    [cacheKey]: validationOptions
                }));
            }

            // First check for exact match (case-insensitive)

            const exactMatch = validationOptions.find(option =>
                option.label && String(option.label).trim().toLowerCase() === String(value).trim().toLowerCase()
            );


            if (exactMatch) {
                return {
                    exists: true,
                    matchType: 'exact',
                    matchedOption: exactMatch
                };
            }

            // If no exact match, try fuzzy matching

            const bestMatch = findBestMatch(value, validationOptions, 0.5);

            if (bestMatch) {
                return {
                    exists: false, // Original value doesn't exist
                    matchType: 'fuzzy',
                    matchedOption: bestMatch,
                    originalValue: value,
                    suggestedValue: bestMatch.label,
                    similarity: bestMatch.similarity
                };
            }

            // No match found
            return {
                exists: false,
                matchType: 'no_match',
                originalValue: value
            };

        } catch (error) {
            console.error('Error checking validation options:', error);
            return { exists: true, matchType: 'error' }; // Assume it exists if we can't check
        }
    };

    const handleProcessPrompt = async () => {
        if (!promptValue.trim() || promptLoading || disabled) return;

        setPromptLoading(true);
        setError(null);
        setCurrentSuggestions(null);

        try {
            const accountId = accountIdFromHook;
            const aiResponse = await aiService.processFormAutoFill(
                promptValue.trim(),
                formContext,
                facilityId,
                accountId,
                activeFacilities
            );

            const newSuggestions = {
                prompt: promptValue.trim(),
                summary: aiResponse.summary || 'AI has analyzed your prompt and found field suggestions.',
                suggestions: aiResponse.suggestions || {},
                reasoning: aiResponse.reasoning || '',
                timestamp: new Date()
            };

            setCurrentSuggestions(newSuggestions);
            setIsEditingPrompt(false);
            // Clear ignored suggestions and fieldsWithCreateNew when new suggestions are loaded
            setIgnoredSuggestions(new Set());
            setFieldsWithCreateNew(new Set());

            // Update parent component with new AI data
            if (onAiDataUpdate) {
                onAiDataUpdate({
                    aiPrompt: newSuggestions.prompt,
                    aiSuggestions: newSuggestions.suggestions,
                    aiSummary: newSuggestions.summary,
                    aiReasoning: newSuggestions.reasoning
                });
            }
        } catch (err) {
            setError(err.message || 'Failed to process AI prompt.');
        } finally {
            setPromptLoading(false);
        }
    };

    const handleEditPrompt = () => {
        setIsEditingPrompt(true);
        setTimeout(() => promptRef.current?.focus(), 100);
    };

    const handleCancelEdit = () => {
        setIsEditingPrompt(false);
        if (currentSuggestions) {
            setPromptValue(currentSuggestions.prompt);
        }
    };

    const handleUpdatePrompt = async () => {
        if (!promptValue.trim() || promptLoading || disabled) return;

        setPromptLoading(true);
        setError(null);
        setCurrentSuggestions(null);

        try {
            const accountId = accountIdFromHook;
            const aiResponse = await aiService.processFormAutoFill(
                promptValue.trim(),
                formContext,
                facilityId,
                accountId,
                activeFacilities
            );

            const newSuggestions = {
                prompt: promptValue.trim(),
                summary: aiResponse.summary || 'AI has analyzed your prompt and found field suggestions.',
                suggestions: aiResponse.suggestions || {},
                reasoning: aiResponse.reasoning || '',
                timestamp: new Date()
            };

            setCurrentSuggestions(newSuggestions);
            setIsEditingPrompt(false);
            // Clear ignored suggestions and fieldsWithCreateNew when suggestions are updated
            setIgnoredSuggestions(new Set());
            setFieldsWithCreateNew(new Set());

            // Update parent component with new AI data
            if (onAiDataUpdate) {
                onAiDataUpdate({
                    aiPrompt: newSuggestions.prompt,
                    aiSuggestions: newSuggestions.suggestions,
                    aiSummary: newSuggestions.summary,
                    aiReasoning: newSuggestions.reasoning
                });
            }
        } catch (err) {
            setError(err.message || 'Failed to process AI prompt.');
        } finally {
            setPromptLoading(false);
        }
    };

    const handleConfirmSuggestions = async () => {
        if (!currentSuggestions?.suggestions || !onApplySuggestions) return;

        const allSuggestions = {};

        // Process each suggestion and check for fuzzy matches
        const suggestionEntries = Object.entries(currentSuggestions.suggestions);

        for (const [fieldAccessor, suggestion] of suggestionEntries) {
            // Safety check: prevent infinite loops
            if (!fieldAccessor || !suggestion) {
                continue;
            }

            // Skip ignored suggestions
            if (ignoredSuggestions.has(fieldAccessor)) {
                continue;
            }

            // Check if field is AI-enabled
            const field = availableFields?.find(f => f.accessor === fieldAccessor);
            const question = questions?.find(q => q.question.accessor === fieldAccessor);

            if (field?.aiEnabled !== false) {
                let value = suggestion.value;

                // Handle date fields - add current year if not provided
                if (field?.type === 'date' && value) {
                    value = processDateValue(value);
                }

                // Handle time fields - convert to 12-hour AM/PM format
                if (field?.type === 'time' && value) {
                    value = processTimeValue(value);
                }

                // For NUMBER_RANGE custom questions, coerce suggestion to a number (drop units/text)
                if (question?.question?.customQuestionInputType === QUESTION_INPUT_TYPE.NUMBER_RANGE && value !== undefined && value !== null) {
                    let numericValue = value;
                    if (typeof numericValue === 'string') {
                        const match = numericValue.match(/-?\d+(?:\.\d+)?/);
                        numericValue = match ? Number(match[0]) : null;
                    }
                    if (typeof numericValue !== 'number' || Number.isNaN(numericValue)) {
                        // Skip invalid numeric suggestion for this field
                        continue;
                    }
                    value = numericValue;
                }

                // For NUMBER_RANGE_LIMITED_ANSWERS custom questions, accept any numeric value
                if (question?.question?.customQuestionInputType === QUESTION_INPUT_TYPE.NUMBER_RANGE_LIMITED_ANSWERS && value !== undefined && value !== null) {
                    try {
                        // Convert word numbers to digits and extract numeric value
                        let numericValue = null;
                        if (typeof value === 'number') {
                            numericValue = value;
                        } else if (typeof value === 'string') {
                            // Convert word numbers to digits first (e.g., "one" -> "1")
                            const convertedValue = convertWordsToNumbers(value);
                            const match = convertedValue.match(/-?\d+(?:\.\d+)?/);
                            if (match) {
                                numericValue = Number(match[0]);
                            }
                        }

                        if (numericValue === null || Number.isNaN(numericValue)) {
                            // Skip invalid numeric suggestion for this field
                            continue;
                        }

                        // For NUMBER_RANGE_LIMITED_ANSWERS, accept any valid numeric value
                        // No need to validate against predefined options - any number is allowed
                        value = numericValue.toString(); // Convert to string for form
                    } catch (error) {
                        // Skip this suggestion on error
                        continue;
                    }
                }

                // For RESIDENCE_LIST custom questions, accept any text value
                if (question?.question?.customQuestionInputType === QUESTION_INPUT_TYPE.RESIDENCE_LIST && value !== undefined && value !== null) {
                    try {

                        // For RESIDENCE_LIST, accept any text value as-is
                        if (typeof value === 'string' && value.trim()) {
                            value = value.trim();
                        } else {
                            console.warn(`❌ Invalid text for RESIDENCE_LIST: ${value}`);
                            // Skip invalid text suggestion for this field
                            continue;
                        }
                    } catch (error) {
                        console.error('Error processing RESIDENCE_LIST:', error);
                        // Skip this suggestion on error
                        continue;
                    }
                }

                // For validation fields, check for fuzzy matches and use matched value if available
                let isCreateNewForValidation = false;
                if (isValidationQuestion(question)) {
                    try {
                        const validationResult = await checkValidationValueAndFindMatch(value, question);

                        // Check if user has made a decision for this fuzzy match
                        const fuzzyDecision = fuzzyDecisions[fieldAccessor];

                        if (validationResult.matchType === 'fuzzy' && validationResult.matchedOption) {
                            // If user decided to apply fuzzy match, use the matched option's ID
                            if (fuzzyDecision?.decision === 'apply') {
                                value = validationResult.matchedOption._id;
                            } else if (fuzzyDecision?.decision === 'cancel') {
                                // User chose to create new instead of accepting the fuzzy match — skip apply.
                                isCreateNewForValidation = true;
                            } else {
                                // Default behavior: use the fuzzy match if similarity is high enough (>= 90%)
                                // Otherwise this is a "Create new" suggestion — skip apply.
                                if (validationResult.similarity >= 0.9) {
                                    value = validationResult.matchedOption._id;
                                } else {
                                    isCreateNewForValidation = true;
                                }
                            }
                        } else if (validationResult.matchType === 'exact' && validationResult.matchedOption) {
                            value = validationResult.matchedOption._id;
                        } else {
                            // 'no_match' (or anything else) means no existing option — "Create new", skip apply.
                            isCreateNewForValidation = true;
                        }
                    } catch (error) {
                        console.error('Error processing validation match:', error);
                        // On error we can't confirm a matched ID, so skip to be safe.
                        isCreateNewForValidation = true;
                    }

                    if (isCreateNewForValidation) {
                        continue;
                    }
                } else if (hasPredefinedOptions(question)) {
                    // For LIMITED_ANSWERS questions, only match against existing predefined options
                    // No new options can be created for these fields
                    try {
                        const predefinedResult = checkPredefinedOptionsAndFindMatch(value, question, findBestMatch);

                        // Check if user has made a decision for this fuzzy match
                        const fuzzyDecision = fuzzyDecisions[fieldAccessor];

                        if (predefinedResult.matchType === 'fuzzy' && predefinedResult.matchedOption) {
                            // If user decided to apply fuzzy match, use the matched option
                            if (fuzzyDecision?.decision === 'apply') {
                                value = predefinedResult.matchedOption.label;
                            } else if (fuzzyDecision?.decision === 'cancel') {
                                // If user decided to cancel fuzzy match, keep original value
                                // value remains as is
                            } else {
                                // Default behavior: use the fuzzy match if similarity is high enough (>= 90%)
                                // Otherwise, keep original value
                                if (predefinedResult.similarity >= 0.9) {
                                    value = predefinedResult.matchedOption.label;
                                }
                                // If similarity < 90%, keep original value
                            }
                        } else if (predefinedResult.matchType === 'exact' && predefinedResult.matchedOption) {
                            value = predefinedResult.matchedOption.label;
                        }
                        // For 'no_match', keep the original value (will show as invalid)
                    } catch (error) {
                        console.error('Error processing predefined options match:', error);
                        // Keep original value on error
                    }
                }

                // Handle multiple value questions - ensure value is an array
                if (isMultipleValueQuestion(question)) {

                    // For multiple value questions, ensure the value is an array
                    if (!Array.isArray(value)) {
                        value = [value]; // Convert single value to array
                    }
                    // Filter out any undefined/null values
                    value = value.filter(v => v !== undefined && v !== null && v !== '');

                }

                allSuggestions[fieldAccessor] = value;
            }
        }

        if (Object.keys(allSuggestions).length > 0) {
            onApplySuggestions(allSuggestions);

            // Show success message instead of clearing suggestions
            setSuccessMessage('Changes applied successfully!');
            setShowSuccessMessage(true);

            // Hide success message after 3 seconds
            setTimeout(() => {
                setShowSuccessMessage(false);
                setSuccessMessage('');
            }, 3000);
        }
    };



    const handlePromptKeyPress = (e) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            e.preventDefault();
            if (isEditingPrompt) {
                handleUpdatePrompt();
            } else {
                handleProcessPrompt();
            }
        }
    };

    // Handler for fuzzy match decisions
    const handleFuzzyMatchDecision = (fieldAccessor, decision, validationResult) => {
        setFuzzyDecisions(prev => ({
            ...prev,
            [fieldAccessor]: {
                decision,
                validationResult
            }
        }));
    };

    // Handler for toggling ignore status of suggestions
    const handleToggleIgnore = (fieldAccessor) => {
        setIgnoredSuggestions(prev => {
            const newSet = new Set(prev);
            if (newSet.has(fieldAccessor)) {
                newSet.delete(fieldAccessor);
            } else {
                newSet.add(fieldAccessor);
            }
            return newSet;
        });
    };

    // Handler for explain why functionality
    const handleExplainWhy = (fieldAccessor, suggestion, fieldLabel) => {
        if (!currentSuggestions?.prompt) {
            return;
        }

        setExplainDialogData({
            fieldAccessor,
            fieldLabel,
            suggestedValue: suggestion.value,
            originalPrompt: currentSuggestions.prompt,
            formContext,
            facilityId
        });
        setExplainDialogOpen(true);
    };

    // Handler for closing explain dialog
    const handleCloseExplainDialog = () => {
        setExplainDialogOpen(false);
        setExplainDialogData(null);
    };

    // Handler for tracking fields that will create new options
    const handleReportCreateNew = React.useCallback((fieldAccessor, willCreateNew) => {
        setFieldsWithCreateNew(prev => {
            const newSet = new Set(prev);
            if (willCreateNew) {
                newSet.add(fieldAccessor);
            } else {
                newSet.delete(fieldAccessor);
            }
            return newSet;
        });
    }, []);

    // Helper function to create new validation option
    const createNewValidationOption = async (value, question) => {
        if (!isValidationQuestion(question)) {
            throw new Error('Question does not support validation options');
        }
        // Get validation type based on question type
        let validationType, validationBase;
        if (question?.question?.type === 'validation') {
            validationType = question.question.validationType;
            validationBase = question.question.validationBase;
        } else if (question?.question?.customQuestionInputType === QUESTION_INPUT_TYPE.UNLIMITED_ANSWERS) {
            // For UNLIMITED_ANSWERS, use the question's validationType (same as regular validation questions)
            validationType = question?.question?.validationType;
            validationBase = question?.question?.validationBase;
        } else {
            throw new Error('Invalid validation type');
        }

        try {
            const response = await axios.post('/api/validation/', {
                facilityId: facilityId,
                label: value,
                active: true,
                type: validationType,
                createdBy: auth._id,
            });

            if (response.data) {
                // Update cache with new validation option
                const cacheKey = `${validationType}_${validationBase || ''}`;
                setValidationOptionsCache(prev => ({
                    ...prev,
                    [cacheKey]: [...(prev[cacheKey] || []), response.data]
                }));

                // Note: Refresh will be called once at the end of batch operations

                return response.data;
            }
            throw new Error('Failed to create validation option');
        } catch (error) {
            console.error('Error creating validation option:', error);
            throw error;
        }
    };


    // Handler for "Confirm with create new option" button
    const handleConfirmWithCreateNew = async () => {
        if (!currentSuggestions?.suggestions || !onApplySuggestions) return;

        setIsCreatingNewOptions(true);
        const allSuggestions = {};
        let hasCreatedNewOptions = false;

        try {
            // Process each suggestion
            for (const [fieldAccessor, suggestion] of Object.entries(currentSuggestions.suggestions)) {
                // Skip ignored suggestions
                if (ignoredSuggestions.has(fieldAccessor)) {
                    continue;
                }

                const field = availableFields?.find(f => f.accessor === fieldAccessor);
                if (field?.aiEnabled !== false) {
                    let value = suggestion.value;
                    const question = questions?.find(q => q.question.accessor === fieldAccessor);

                    // Handle date fields - add current year if not provided
                    if (field?.type === 'date' && value) {
                        value = processDateValue(value);
                    }

                    // Handle time fields - convert to 12-hour AM/PM format
                    if (field?.type === 'time' && value) {
                        value = processTimeValue(value);
                    }

                    // For validation fields, handle creation and matching
                    // LIMITED_ANSWERS questions are not validation questions, so they won't be processed here
                    if (isValidationQuestion(question)) {
                        try {
                            const validationResult = await checkValidationValueAndFindMatch(value, question);
                            const fuzzyDecision = fuzzyDecisions[fieldAccessor];

                            if (validationResult.matchType === 'fuzzy' && validationResult.matchedOption) {
                                if (fuzzyDecision?.decision === 'apply') {
                                    value = validationResult.matchedOption._id;
                                } else if (fuzzyDecision?.decision === 'cancel') {
                                    // Create new option
                                    const newOption = await createNewValidationOption(value, question);

                                    value = newOption._id;
                                    hasCreatedNewOptions = true;
                                } else {
                                    // Default behavior based on similarity
                                    if (validationResult.similarity >= 0.9) {
                                        value = validationResult.matchedOption._id;
                                    } else {
                                        // Create new option for low similarity case
                                        const newOption = await createNewValidationOption(value, question);
                                        value = newOption._id;
                                        hasCreatedNewOptions = true;
                                    }
                                }
                            } else if (validationResult.matchType === 'exact' && validationResult.matchedOption) {
                                value = validationResult.matchedOption._id;
                            } else if (validationResult.matchType === 'no_match') {
                                // Create new option
                                const newOption = await createNewValidationOption(value, question);
                                value = newOption._id;
                                hasCreatedNewOptions = true;
                            }
                        } catch (error) {
                            console.error('Error processing validation for field:', fieldAccessor, error);
                            // Keep original value on error
                        }
                    } else if (hasPredefinedOptions(question)) {
                        // For predefined options, only match against existing options
                        try {
                            const predefinedResult = checkPredefinedOptionsAndFindMatch(value, question, findBestMatch);
                            const fuzzyDecision = fuzzyDecisions[fieldAccessor];

                            if (predefinedResult.matchType === 'fuzzy' && predefinedResult.matchedOption) {
                                if (fuzzyDecision?.decision === 'apply') {
                                    value = predefinedResult.matchedOption.label;
                                } else if (fuzzyDecision?.decision === 'cancel') {
                                    // Keep original value (will show as invalid)
                                    // value remains as is
                                } else {
                                    // Default behavior based on similarity
                                    if (predefinedResult.similarity >= 0.9) {
                                        value = predefinedResult.matchedOption.label;
                                    }
                                    // If similarity < 90%, keep original value
                                }
                            } else if (predefinedResult.matchType === 'exact' && predefinedResult.matchedOption) {
                                value = predefinedResult.matchedOption.label;
                            }
                            // For 'no_match', keep the original value (will show as invalid)
                        } catch (error) {
                            console.error('Error processing predefined options for field:', fieldAccessor, error);
                            // Keep original value on error
                        }
                    }

                    // Handle multiple value questions - ensure value is an array
                    if (isMultipleValueQuestion(question)) {
                        // For multiple value questions, ensure the value is an array
                        if (!Array.isArray(value)) {
                            value = [value]; // Convert single value to array
                        }
                        // Filter out any undefined/null values
                        value = value.filter(v => v !== undefined && v !== null && v !== '');
                    }

                    allSuggestions[fieldAccessor] = value;
                }
            }

            if (Object.keys(allSuggestions).length > 0) {
                await onApplySuggestions(allSuggestions);

                // Refresh components if we created new validation options
                if (hasCreatedNewOptions && refreshComponent) {
                    refreshComponent();
                }

                // Show success message instead of clearing suggestions
                setSuccessMessage('Changes applied successfully with new options created!');
                setShowSuccessMessage(true);

                // Hide success message after 3 seconds
                setTimeout(() => {
                    setShowSuccessMessage(false);
                    setSuccessMessage('');
                }, 3000);

                // Reset fuzzy decisions but keep suggestions visible
                setFuzzyDecisions({});
            }
        } catch (error) {
            console.error('Error in handleConfirmWithCreateNew:', error);
            setError(error.message || 'Failed to create new validation options.');
        } finally {
            setIsCreatingNewOptions(false);
        }
    };

    return (
        <Paper className={styles.aiChatSidebar} elevation={2}>
            {/* Header */}
            <div className={styles.header}>
                <Box display="flex" alignItems="center" gap={1}>
                    <AIIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle2" color="primary">
                        AI Assistant
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                    {/* <IconButton size="small" onClick={handleClearPrompt} title="Clear All">
                        <ClearIcon fontSize="small" />
                    </IconButton> */}
                    {onClose && (
                        <IconButton size="small" onClick={onClose} title="Close AI Assistant">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            </div>

            <Divider />

            {/* Top Prompt Input Area */}
            <div className={styles.promptArea}>
                <div className={styles.textareaContainer}>
                    <textarea
                        ref={promptRef}
                        className={styles.promptTextarea}
                        placeholder="Describe patient information to auto-fill fields... (Press Ctrl+Enter to process)"
                        value={promptValue}
                        onChange={(e) => setPromptValue(e.target.value)}
                        onKeyDown={handlePromptKeyPress}
                        disabled={promptLoading || disabled}
                        rows={4}
                    />
                    <div className={styles.textareaFooter}>
                        <span className={styles.characterCount}>
                            {promptValue.length} characters
                        </span>
                        <span className={styles.shortcutHint}>
                            {isEditingPrompt ? 'Ctrl+Enter to update' : 'Ctrl+Enter to analyze'}
                        </span>
                    </div>
                </div>
                <Box className={styles.promptActions}>
                    {isEditingPrompt ? (
                        <Box display="flex" gap={1}>
                            <Button
                                variant="contained"
                                startIcon={promptLoading ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                                onClick={handleUpdatePrompt}
                                disabled={!promptValue.trim() || promptLoading || disabled}
                                size="small"
                                color="primary"
                            >
                                {promptLoading ? 'Processing...' : 'Update'}
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={handleCancelEdit}
                                disabled={promptLoading}
                            >
                                Cancel
                            </Button>
                        </Box>
                    ) : (
                        <Button
                            variant="contained"
                            startIcon={promptLoading ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                            onClick={handleProcessPrompt}
                            disabled={!promptValue.trim() || promptLoading || disabled}
                            size="small"
                            color="primary"
                        >
                            {promptLoading ? 'Processing...' : 'Analyze'}
                        </Button>
                    )}
                </Box>
            </div>

            {/* Current Suggestions Display */}
            {currentSuggestions && (
                <>
                    <Divider />
                    <div className={styles.suggestionsDisplay}>
                        <div className={styles.suggestionsDisplayHeader} style={{ display: 'block' }}>
                            {/* First row - Field Suggestions text */}
                            <div style={{ display: 'block', marginBottom: '16px' }}>
                                <Typography variant="subtitle2" color="primary" sx={{
                                    fontWeight: 600,
                                    fontSize: '14px',
                                    whiteSpace: 'nowrap',
                                    display: 'block'
                                }}>
                                    Field Suggestions
                                </Typography>
                            </div>

                            {/* Second row - Action buttons */}
                            <div style={{ display: 'block' }}>
                                <Box display="flex" gap={1} flexWrap="wrap">
                                    {!isEditingPrompt && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={handleEditPrompt}
                                            startIcon={<EditIcon />}
                                            sx={{
                                                borderColor: '#1976d2',
                                                color: '#1976d2',
                                                '&:hover': {
                                                    borderColor: '#1565c0',
                                                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                                }
                                            }}
                                        >
                                            Edit
                                        </Button>
                                    )}
                                    <Button
                                        size="small"
                                        variant="contained"
                                        onClick={handleConfirmSuggestions}
                                        startIcon={<CheckCircleIcon />}
                                        color="success"
                                        disabled={Object.entries(currentSuggestions.suggestions).filter(([fieldAccessor]) => {
                                            const field = availableFields?.find(f => f.accessor === fieldAccessor);
                                            return field?.aiEnabled !== false && !ignoredSuggestions.has(fieldAccessor);
                                        }).length === 0}
                                        sx={{
                                            backgroundColor: '#4caf50',
                                            '&:hover': {
                                                backgroundColor: '#45a049'
                                            }
                                        }}
                                    >
                                        Confirm All
                                        {ignoredSuggestions.size > 0 && (
                                            <Typography variant="caption" sx={{
                                                marginLeft: 1,
                                                fontSize: '10px',
                                                opacity: 0.8
                                            }}>
                                                ({Object.entries(currentSuggestions.suggestions).filter(([fieldAccessor]) => {
                                                    const field = availableFields?.find(f => f.accessor === fieldAccessor);
                                                    return field?.aiEnabled !== false && !ignoredSuggestions.has(fieldAccessor);
                                                }).length} of {Object.entries(currentSuggestions.suggestions).filter(([fieldAccessor]) => {
                                                    const field = availableFields?.find(f => f.accessor === fieldAccessor);
                                                    return field?.aiEnabled !== false;
                                                }).length})
                                            </Typography>
                                        )}
                                    </Button>

                                    {/* Show "Confirm with create new option" button if there are validation fields that will create new options */}
                                    {(() => {
                                        // Check if there are any validation fields that will create new options
                                        // This checks two cases:
                                        // 1. Fields that are tracked in fieldsWithCreateNew (reported by SuggestionItem showing "Create new")
                                        // 2. Fields where user explicitly clicked cancel on fuzzy match
                                        const hasValidationFieldsNeedingCreation = Object.entries(currentSuggestions.suggestions).some(([fieldAccessor, suggestion]) => {
                                            // Skip ignored suggestions
                                            if (ignoredSuggestions.has(fieldAccessor)) {
                                                return false;
                                            }

                                            const field = availableFields?.find(f => f.accessor === fieldAccessor);

                                            // Only check AI-enabled fields
                                            if (field?.aiEnabled === false) {
                                                return false;
                                            }

                                            // Case 1: Field is tracked as showing "Create new" indicator
                                            // This is reported by SuggestionItem when it shows "Create new"
                                            if (fieldsWithCreateNew.has(fieldAccessor)) {
                                                return true;
                                            }

                                            // Case 2: User explicitly clicked cancel on fuzzy match (X icon)
                                            // This means they rejected the suggestion and want to create new
                                            const fuzzyDecision = fuzzyDecisions[fieldAccessor];
                                            if (fuzzyDecision?.decision === 'cancel') {
                                                return true;
                                            }

                                            return false;
                                        });

                                        return hasValidationFieldsNeedingCreation && (
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={handleConfirmWithCreateNew}
                                                startIcon={isCreatingNewOptions ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                                                disabled={isCreatingNewOptions}
                                                sx={{
                                                    backgroundColor: '#ff9800',
                                                    color: 'white',
                                                    '&:hover': {
                                                        backgroundColor: '#f57c00'
                                                    },
                                                    '&:disabled': {
                                                        backgroundColor: '#ffcc80',
                                                        color: '#fff'
                                                    }
                                                }}
                                            >
                                                {isCreatingNewOptions ? 'Creating...' : 'Confirm with Create New'}
                                            </Button>
                                        );
                                    })()}
                                </Box>
                            </div>
                        </div>

                        <div className={styles.suggestionsContent}>
                            {/* Suggestions Summary */}
                            {currentSuggestions.summary && (
                                <Alert severity="info" className={styles.summaryAlert}>
                                    {currentSuggestions.summary}
                                </Alert>
                            )}

                            {/* Suggestions List */}
                            <div className={styles.suggestionsList}>
                                {Object.entries(currentSuggestions.suggestions)
                                    .filter(([fieldAccessor, suggestion]) => {
                                        const field = availableFields?.find(f => f.accessor === fieldAccessor);
                                        return field?.aiEnabled !== false; // Only show AI-enabled fields
                                    })
                                    .map(([fieldAccessor, suggestion]) => {
                                        const field = availableFields?.find(f => f.accessor === fieldAccessor);
                                        const fieldLabel = field?.label || fieldAccessor;
                                        const question = questions?.find(q => q.question.accessor === fieldAccessor);
                                        const isIgnored = ignoredSuggestions.has(fieldAccessor);

                                        // Process date and time values for display
                                        let displayValue = suggestion.value;
                                        if (field?.type === 'date' && suggestion.value) {
                                            displayValue = processDateValue(suggestion.value);
                                        } else if (field?.type === 'time' && suggestion.value) {
                                            displayValue = processTimeValue(suggestion.value);
                                        }

                                        // For validation fields, always show the original text, not the processed ID
                                        if (isValidationQuestion(question)) {
                                            // Always use the original suggestion value (text) for display
                                            displayValue = suggestion.value;
                                        }

                                        return (
                                            <SuggestionItem
                                                key={fieldAccessor}
                                                fieldAccessor={fieldAccessor}
                                                fieldLabel={fieldLabel}
                                                displayValue={displayValue}
                                                suggestion={suggestion}
                                                question={question}
                                                availableFields={availableFields}
                                                checkValidationValueAndFindMatch={checkValidationValueAndFindMatch}
                                                checkPredefinedOptionsAndFindMatch={checkPredefinedOptionsAndFindMatch}
                                                findBestMatch={findBestMatch}
                                                onFuzzyMatchDecision={handleFuzzyMatchDecision}
                                                isIgnored={isIgnored}
                                                onToggleIgnore={handleToggleIgnore}
                                                onExplainWhy={handleExplainWhy}
                                                onReportCreateNew={handleReportCreateNew}
                                            />
                                        );
                                    })}
                            </div>

                            {Object.entries(currentSuggestions.suggestions).filter(([fieldAccessor]) => {
                                const field = availableFields?.find(f => f.accessor === fieldAccessor);
                                return field?.aiEnabled !== false;
                            }).length === 0 && (
                                    <Alert severity="warning" className={styles.noSuggestionsAlert}>
                                        Try being more specific about the patient information.
                                    </Alert>
                                )}
                        </div>
                    </div>
                </>
            )}

            <Divider />

            {/* Success Alert */}
            {showSuccessMessage && (
                <Alert severity="success" className={styles.successAlert} onClose={() => setShowSuccessMessage(false)}>
                    {successMessage}
                </Alert>
            )}

            {/* Error Alert */}
            {error && (
                <Alert severity="error" className={styles.errorAlert} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Explain Why Dialog */}
            {explainDialogData && (
                <ExplainWhyDialog
                    open={explainDialogOpen}
                    onClose={handleCloseExplainDialog}
                    fieldAccessor={explainDialogData.fieldAccessor}
                    fieldLabel={explainDialogData.fieldLabel}
                    suggestedValue={explainDialogData.suggestedValue}
                    originalPrompt={explainDialogData.originalPrompt}
                    formContext={explainDialogData.formContext}
                    facilityId={explainDialogData.facilityId}
                />
            )}
        </Paper>
    );
};

export default AiChatSidebar;
